// components/LeadEmailThread.jsx

import React, { useState, useEffect } from "react";
import {
  Mail,
  Reply,
  Clock,
  User,
  ChevronDown,
  ChevronRight,
  Loader,
} from "lucide-react";
import apiService from "../services/api";
import { useTheme } from "../contexts/ThemeContext";

const LeadEmailThread = ({ leadId, leadData }) => {
  const { isDarkMode } = useTheme();
  const [emails, setEmails] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedEmails, setExpandedEmails] = useState(new Set());
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyBody, setReplyBody] = useState("");

  useEffect(() => {
    if (leadId) {
      fetchEmails();
    }
  }, [leadId]);

  const fetchEmails = async () => {
    try {
      setLoading(true);
      const response = await apiService.getLeadEmailThread(leadId);
      setEmails(response.data.rows || []);
    } catch (error) {
      console.error("Error fetching emails:", error);
    } finally {
      setLoading(false);
    }
  };

  const toggleEmail = (emailId) => {
    setExpandedEmails((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(emailId)) {
        newSet.delete(emailId);
      } else {
        newSet.add(emailId);
      }
      return newSet;
    });
  };

  const handleReply = async (emailId) => {
    if (!replyBody.trim()) {
      alert("Please enter a reply message");
      return;
    }

    try {
      await apiService.replyToLeadEmail(leadId, emailId, {
        body: replyBody,
        bodyHtml: replyBody,
      });

      alert("Reply sent successfully!");
      setReplyingTo(null);
      setReplyBody("");
      fetchEmails();
    } catch (error) {
      console.error("Error sending reply:", error);
      alert("Failed to send reply");
    }
  };

  const renderEmail = (email, depth = 0) => {
    const isExpanded = expandedEmails.has(email.id);
    const isReplying = replyingTo === email.id;

    return (
      <div
        key={email.id}
        className={`${
          depth > 0
            ? `ml-8 mt-4 border-l-2 pl-4 ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`
            : "mb-4"
        }`}
      >
        <div
          className={`border rounded-lg overflow-hidden ${
            isDarkMode
              ? "bg-[#13161B] border-gray-700"
              : "bg-white border-gray-200"
          }`}
        >
          <div
            className={`p-4 cursor-pointer transition-colors ${
              isDarkMode ? "hover:bg-gray-800/50" : "hover:bg-gray-50"
            }`}
            onClick={() => toggleEmail(email.id)}
          >
            <div className="flex items-start justify-between">
              <div className="flex-1">
                <div className="flex items-center gap-2 mb-2">
                  <div
                    className={`w-8 h-8 rounded-full flex items-center justify-center text-white text-sm font-medium ${
                      isDarkMode ? "bg-blue-600" : "bg-blue-500"
                    }`}
                  >
                    {email.sender?.name?.charAt(0) || email.from?.charAt(0)}
                  </div>
                  <div>
                    <div
                      className={`font-medium ${
                        isDarkMode ? "text-white" : "text-gray-900"
                      }`}
                    >
                      {email.sender?.name || email.from}
                    </div>
                    <div
                      className={`text-xs ${
                        isDarkMode ? "text-gray-400" : "text-gray-500"
                      }`}
                    >
                      {email.from}
                    </div>
                  </div>
                </div>
                <div
                  className={`text-sm mb-1 ${
                    isDarkMode ? "text-gray-300" : "text-gray-700"
                  }`}
                >
                  {email.subject}
                </div>
                <div
                  className={`flex items-center gap-4 text-xs ${
                    isDarkMode ? "text-gray-400" : "text-gray-500"
                  }`}
                >
                  <span className="flex items-center gap-1">
                    <Clock size={12} />
                    {new Date(email.sentAt || email.createdAt).toLocaleString()}
                  </span>
                  <span>To: {email.to}</span>
                  {email.openedAt && (
                    <span className="text-green-500">Opened</span>
                  )}
                  {email.repliedAt && (
                    <span className="text-blue-500">Replied</span>
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    setReplyingTo(isReplying ? null : email.id);
                  }}
                  className={`p-2 rounded-lg transition-colors ${
                    isDarkMode ? "hover:bg-gray-700" : "hover:bg-gray-100"
                  }`}
                  title="Reply"
                >
                  <Reply
                    size={16}
                    className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                  />
                </button>
                {isExpanded ? (
                  <ChevronDown
                    size={20}
                    className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                  />
                ) : (
                  <ChevronRight
                    size={20}
                    className={isDarkMode ? "text-gray-400" : "text-gray-500"}
                  />
                )}
              </div>
            </div>
          </div>

          {isExpanded && (
            <div
              className={`border-t p-4 ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <div
                className={`text-sm whitespace-pre-wrap ${
                  isDarkMode ? "text-gray-300" : "text-gray-700"
                }`}
                dangerouslySetInnerHTML={{
                  __html: email.bodyHtml || email.body,
                }}
              />
            </div>
          )}

          {isReplying && (
            <div
              className={`border-t p-4 ${
                isDarkMode ? "border-gray-700" : "border-gray-200"
              }`}
            >
              <textarea
                value={replyBody}
                onChange={(e) => setReplyBody(e.target.value)}
                placeholder="Write your reply..."
                rows={4}
                className={`w-full px-3 py-2 border rounded-lg resize-none focus:outline-none focus:ring-2 focus:ring-blue-500 mb-3 ${
                  isDarkMode
                    ? "bg-[#0C0E12] border-gray-700 text-white placeholder-gray-500"
                    : "bg-white border-gray-300 text-gray-900 placeholder-gray-400"
                }`}
              />
              <div className="flex gap-2">
                <button
                  onClick={() => handleReply(email.id)}
                  className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                    isDarkMode
                      ? "bg-blue-600 hover:bg-blue-700 text-white"
                      : "bg-blue-500 hover:bg-blue-600 text-white"
                  }`}
                >
                  Send Reply
                </button>
                <button
                  onClick={() => {
                    setReplyingTo(null);
                    setReplyBody("");
                  }}
                  className={`px-4 py-2 rounded-lg transition-colors text-sm ${
                    isDarkMode
                      ? "bg-gray-700 hover:bg-gray-600 text-white"
                      : "bg-gray-200 hover:bg-gray-300 text-gray-900"
                  }`}
                >
                  Cancel
                </button>
              </div>
            </div>
          )}
        </div>

        {/* Render replies */}
        {email.replies && email.replies.length > 0 && (
          <div className="mt-2">
            {email.replies.map((reply) => renderEmail(reply, depth + 1))}
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <Loader
          className={`animate-spin ${
            isDarkMode ? "text-gray-400" : "text-gray-500"
          }`}
          size={32}
        />
      </div>
    );
  }

  if (emails.length === 0) {
    return (
      <div className="text-center py-12">
        <Mail
          size={48}
          className={`mx-auto mb-4 ${
            isDarkMode ? "text-gray-600" : "text-gray-300"
          }`}
        />
        <p className={isDarkMode ? "text-gray-400" : "text-gray-500"}>
          No emails yet
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">{emails.map((email) => renderEmail(email))}</div>
  );
};

export default LeadEmailThread;
